import { Link, useNavigate } from '@tanstack/react-router';
import {
    ArrowLeft,
    CalendarIcon,
    Clock,
    ExternalLink,
    Loader2,
    MessageSquareText,
    X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type KeyedMutator } from 'swr';

import { BlockedPhonePopover } from '@apps/web/components/caregivers/blocked_phone_popover';
import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { OutreachSubscribersPanel } from '@apps/web/components/outreach/outreach_subscribers_panel';
import { AddCaregiversModal } from '@apps/web/components/outreaches/add_caregivers_modal';
import { AssignAndFillConfirmationModal } from '@apps/web/components/outreaches/assign_and_fill_confirmation_modal';
import { CancelOutreachDialog } from '@apps/web/components/outreaches/cancel_outreach_dialog';
import { CaregiverOutreachDetailModal } from '@apps/web/components/outreaches/caregiver_outreach_detail_modal';
import { ClockReminderOutcomeBadge } from '@apps/web/components/outreaches/clock_reminder_outcome_badge';
import {
    formatClockReminderResolution,
    getClockReminderResolution,
} from '@apps/web/components/outreaches/clock_reminder_resolution';
import { ContactAttemptStatusBadge } from '@apps/web/components/outreaches/contact_attempt_status_badge';
import { OutreachStepsModal } from '@apps/web/components/outreaches/outreach_steps_modal';
import { OutreachTypeBadge } from '@apps/web/components/outreaches/outreach_type_badge';
import {
    getContactAttemptDetailsText,
    getOverallCaregiverResponse,
    isActiveOutreach,
} from '@apps/web/components/outreaches/utils';
import { PendingMessagesDialog } from '@apps/web/components/shared/messages/messages_pending_dialog';
import { EditShiftModal } from '@apps/web/components/shifts/edit_shift_modal';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    DataTable,
    DataTableColumnHeader,
} from '@apps/web/components/ui/data_table';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { VoiceCallAudioPlayer } from '@apps/web/components/voice_calls/voice_call_audio_player';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import {
    useContactAttemptsRealtime,
    useOrganizationPendingMessagesCountRealtime,
    useOrganizationPendingMessagesRealtime,
} from '@apps/web/hooks/realtime_hooks';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    compareCaregiverDisplayName,
    formatCaregiverName,
} from '@apps/web/lib/caregiver_name';
import { getDeliveryModeDisplayName } from '@apps/web/lib/contact_utils';
import {
    generateExternalCaregiverUrl,
    generateExternalClientUrl,
    getDataSourceDisplayName,
} from '@apps/web/lib/data_source_utils';
import {
    formatDateAndTime,
    formatTime,
    getDateStringInTimezone,
    zonedTimeToUtcIso,
} from '@apps/web/lib/date_utils';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import { type ColumnDef } from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';
import {
    type BlockedCaregiverInfo,
    useBlockedCaregivers,
    useOrganization,
    useOrganizationClients,
    useOrganizationDataSourceAccounts,
    useUser,
    useVoiceCallRecordingUrls,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type CaregiverResponseType,
    type components,
    type ContactAttempt,
    type ContactAttemptOutreachStatus,
    type JoinedOutreachRead,
    type OrganizationDataSourceAccount,
    type OrgCaregiverRecord,
    type OutreachStatus,
    type ShiftAssignmentType,
} from '@libraries/typescript/api_client';

type ClockReminderOutreachDetailProps = {
    readonly outreach: JoinedOutreachRead;
    readonly mutateOutreach: KeyedMutator<JoinedOutreachRead>;
    readonly onOpenOutreachChat?: () => void;
    readonly isOutreachChatAvailable?: boolean;
    readonly selectedContactAttemptId?: string | null;
};

type OrgClientRead = components['schemas']['OrgClientRead'];

// Helper function to determine if an outreach has started.
function isOutreachStarted(status: OutreachStatus): boolean {
    return status !== 'pending';
}

type ContactAttemptGroup = {
    readonly caregiver: OrgCaregiverRecord;
    readonly attempts: Array<ContactAttempt>;
    readonly overallResponse: CaregiverResponseType | null;
    readonly recordedResponse: CaregiverResponseType | null;
    readonly hasOverride?: boolean;
};

const VOICE_OUTREACH_STATUSES_EXPECTING_SUMMARY = new Set<
    ContactAttempt['outreach_status']
>(['completed', 'live_answer']);

const NON_TERMINAL_CONTACT_ATTEMPT_STATUSES =
    new Set<ContactAttemptOutreachStatus>([
        'queued',
        'initiated',
        'in_progress',
        'reviewing_call',
    ]);

const CAREGIVER_RESPONSE_LABELS: Record<CaregiverResponseType, string> = {
    available_interested: 'Available',
    available_not_interested: 'Not a fit',
    not_a_fit: 'Not a fit',
    unavailable: 'Unavailable',
    pending: 'Pending response',
    no_response: 'No response',
    unknown: 'Needs review',
};

function getCaregiverResponseLabel(response: CaregiverResponseType | null) {
    return response ? CAREGIVER_RESPONSE_LABELS[response] : '—';
}

const shouldShowSummaryPending = (attempt: ContactAttempt): boolean => {
    if (attempt.delivery_mode !== 'voice' || attempt.call_summary) {
        return false;
    }

    if (
        !VOICE_OUTREACH_STATUSES_EXPECTING_SUMMARY.has(attempt.outreach_status)
    ) {
        return false;
    }

    const callStatus = attempt.voice_call?.call_status;

    if (!callStatus) {
        return true;
    }

    return callStatus === 'ended';
};

const createContactAttemptsColumns = (
    timezone: string,
    hasPendingMessage?: (caregiverId: string) => boolean,
    formatCaregiverNameFn: (name: string) => string = (name) => name,
    options?: {
        isDevelopment: boolean;
        onDeleteContactAttempt: (contactAttemptId: string) => void;
        deletingContactAttemptId: string | null;
        outreachSteps?: ReadonlyArray<OutreachStep>;
        blockedCaregiverMap?: Map<string, Array<BlockedCaregiverInfo>>;
        onCaregiverUnblocked?: () => void;
        onCaregiverClick?: (caregiverId: string) => void;
        recordingUrls?: Record<string, string>;
        dataSourceAccounts?: Array<OrganizationDataSourceAccount>;
        sortByLastName?: boolean;
    },
): Array<ColumnDef<ContactAttemptGroup>> => {
    const outreachSteps = options?.outreachSteps;
    const blockedCaregiverMap = options?.blockedCaregiverMap;
    const onCaregiverUnblocked = options?.onCaregiverUnblocked;
    const recordingUrls = options?.recordingUrls;
    const dataSourceAccounts = options?.dataSourceAccounts;

    return [
        {
            id: 'pendingMessage',
            header: '',
            cell: ({ row }) => {
                const hasPending = hasPendingMessage?.(
                    row.original.caregiver.id,
                );
                if (!hasPending) {
                    return null;
                }

                return (
                    <div className="flex items-center justify-center">
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <div className="relative cursor-pointer">
                                    <MessageSquareText className="h-4 w-4 text-amber-600" />
                                    <div className="absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full bg-amber-600" />
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>New message awaiting approval</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                );
            },
            size: 40,
        },
        {
            id: 'externalLink',
            header: '',
            cell: ({ row }) => {
                const sourceSystem = row.original.caregiver.source_system;
                const externalId = row.original.caregiver.external_id;
                if (!sourceSystem || !externalId) {
                    return null;
                }

                const dataSourceAccount = dataSourceAccounts?.find(
                    (account) => account.type === sourceSystem,
                );
                if (!dataSourceAccount) {
                    return null;
                }

                const externalUrl = generateExternalCaregiverUrl(
                    sourceSystem,
                    externalId,
                    dataSourceAccount.base_url,
                );
                if (!externalUrl) {
                    return null;
                }

                const systemName = getDataSourceDisplayName(sourceSystem);

                return (
                    <Button
                        variant="ghost"
                        size="icon"
                        asChild={true}
                        className="h-8 w-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <a
                            href={externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`View in ${systemName}`}
                        >
                            <ExternalLink className="h-4 w-4" />
                            <span className="sr-only">
                                View in {systemName}
                            </span>
                        </a>
                    </Button>
                );
            },
            size: 40,
        },
        {
            accessorKey: 'caregiver.name',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Caregiver" />
            ),
            sortFn: (rowA, rowB, columnId) => {
                const nameA = rowA.getValue<string>(columnId) ?? '';
                const nameB = rowB.getValue<string>(columnId) ?? '';
                return compareCaregiverDisplayName(
                    nameA,
                    nameB,
                    formatCaregiverNameFn,
                    options?.sortByLastName ?? false,
                );
            },
            enableSorting: true,
            cell: ({ row }) => {
                const blockedInfos = blockedCaregiverMap?.get(
                    row.original.caregiver.id,
                );
                return (
                    <div>
                        <div className="flex items-center gap-2 font-medium">
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    options?.onCaregiverClick?.(
                                        row.original.caregiver.id,
                                    );
                                }}
                                className="cursor-pointer text-left font-medium underline"
                            >
                                {formatCaregiverNameFn(
                                    row.original.caregiver.name,
                                )}
                            </button>
                            {blockedInfos &&
                                blockedInfos.length > 0 &&
                                onCaregiverUnblocked && (
                                    <BlockedPhonePopover
                                        blockedInfos={blockedInfos}
                                        onUnblocked={onCaregiverUnblocked}
                                    />
                                )}
                        </div>
                        <div className="text-muted-foreground text-sm">
                            {row.original.attempts.length} attempt
                            {row.original.attempts.length === 1 ? '' : 's'}
                        </div>
                    </div>
                );
            },
        },
        {
            id: 'contactAttempts',
            header: 'Contact Attempts',
            cell: ({ row }) => (
                <Table className="table-fixed">
                    <TableHeader>
                        <TableRow className="border-0">
                            <TableHead className="w-28 px-2">
                                Delivery Mode
                            </TableHead>
                            <TableHead className="w-32 px-2">
                                Outreach Status
                            </TableHead>
                            <TableHead className="w-40 px-2">Created</TableHead>
                            {outreachSteps && (
                                <TableHead className="w-32 px-2">
                                    Next Attempt
                                </TableHead>
                            )}
                            <TableHead className="w-48 px-2">
                                Summary / Transcript
                            </TableHead>
                            <TableHead className="w-48 px-2">
                                Recording
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {row.original.attempts.map((attempt) => (
                            <TableRow key={attempt.id}>
                                <TableCell className="w-28 px-2">
                                    <Badge
                                        variant="outline"
                                        className="text-xs"
                                    >
                                        {getDeliveryModeDisplayName(
                                            attempt.delivery_mode,
                                        )}
                                    </Badge>
                                </TableCell>
                                <TableCell className="w-32 px-2">
                                    <ContactAttemptStatusBadge
                                        status={attempt.outreach_status}
                                        deliveryMode={attempt.delivery_mode}
                                        communicationDetails={
                                            attempt.communication_details
                                        }
                                    />
                                </TableCell>
                                <TableCell className="text-muted-foreground w-40 px-2 text-xs">
                                    {formatDateAndTime(
                                        attempt.created_at,
                                        timezone,
                                    )}
                                </TableCell>
                                {outreachSteps && (
                                    <TableCell className="w-32 px-2">
                                        <NextAttemptCountdown
                                            attempt={attempt}
                                            outreachSteps={outreachSteps}
                                        />
                                    </TableCell>
                                )}
                                <TableCell className="w-48 overflow-hidden px-2">
                                    {shouldShowSummaryPending(attempt) ? (
                                        <div className="text-muted-foreground flex items-center gap-2 text-xs">
                                            <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />
                                            <span className="truncate">
                                                Generating summary…
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="text-muted-foreground block truncate text-xs">
                                            {getContactAttemptDetailsText(
                                                attempt,
                                            )}
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className="w-48 overflow-hidden px-2">
                                    {attempt.voice_call ? (
                                        <VoiceCallAudioPlayer
                                            voiceCall={attempt.voice_call}
                                            prefetchedRecordingUrl={
                                                recordingUrls?.[
                                                    attempt.voice_call.id
                                                ]
                                            }
                                        />
                                    ) : (
                                        <span className="text-muted-foreground text-xs">
                                            —
                                        </span>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ),
        },
    ];
};

const formatCountdownLabel = (seconds: number): string => {
    const safeSeconds = Math.max(0, seconds);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes}m:${remainingSeconds.toString().padStart(2, '0')}s`;
};

type OutreachStep = {
    readonly step_type: ContactAttempt['delivery_mode'];
    readonly delay_in_minutes: number;
    readonly message_template?: string | null;
    readonly prompt_alteration?: string | null;
};

type NextAttemptCountdownProps = {
    readonly attempt: ContactAttempt;
    readonly outreachSteps: ReadonlyArray<OutreachStep>;
};

function NextAttemptCountdown({
    attempt,
    outreachSteps,
}: NextAttemptCountdownProps) {
    const [countdownSeconds, setCountdownSeconds] = useState<number | null>(
        null,
    );

    const nextAttemptTime = useMemo(() => {
        // Don't show countdown if attempt is still in progress
        if (
            NON_TERMINAL_CONTACT_ATTEMPT_STATUSES.has(attempt.outreach_status)
        ) {
            return null;
        }

        if (!outreachSteps || outreachSteps.length === 0) {
            return null;
        }

        const currentRound =
            typeof attempt.round === 'number' ? attempt.round : -1;

        if (currentRound === -1) {
            return null;
        }

        const nextStep = outreachSteps[currentRound + 1];
        if (!nextStep) {
            return null;
        }

        const waitMinutes = nextStep.delay_in_minutes;
        if (waitMinutes <= 0) {
            return null;
        }

        const attemptCreatedAt = new Date(attempt.created_at);
        if (Number.isNaN(attemptCreatedAt.getTime())) {
            return null;
        }

        const target = new Date(attemptCreatedAt);
        target.setMinutes(target.getMinutes() + waitMinutes);

        return {
            target,
            deliveryMode: nextStep.step_type,
        };
    }, [attempt, outreachSteps]);

    useEffect(() => {
        if (!nextAttemptTime) {
            setCountdownSeconds(null);
            return;
        }

        const updateCountdown = () => {
            const now = new Date();
            const diff = nextAttemptTime.target.getTime() - now.getTime();

            if (diff <= 0) {
                setCountdownSeconds((previous) =>
                    previous === 0 ? previous : 0,
                );
                return true;
            }

            setCountdownSeconds(Math.ceil(diff / 1000));
            return false;
        };

        const shouldStopImmediately = updateCountdown();
        if (shouldStopImmediately) {
            return;
        }

        const interval = setInterval(() => {
            const shouldStop = updateCountdown();
            if (shouldStop) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [nextAttemptTime]);

    if (!nextAttemptTime || countdownSeconds === null) {
        return <span className="text-muted-foreground text-xs">-</span>;
    }

    return (
        <div className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
            <span className={countdownSeconds > 0 ? 'text-blue-600' : ''}>
                {formatCountdownLabel(countdownSeconds)}
            </span>
        </div>
    );
}

type OutreachDetailsProps = {
    readonly organizationId: string | null;
    readonly outreach: JoinedOutreachRead;
    readonly timezone: string;
    readonly mutateOutreach: KeyedMutator<JoinedOutreachRead>;
};

type MobileDetailCardProps = {
    readonly label: React.ReactNode;
    readonly children: React.ReactNode;
};

function MobileDetailCard({ label, children }: MobileDetailCardProps) {
    return (
        <div className="rounded-lg border p-4">
            <div className="text-muted-foreground mb-2 text-xs font-medium">
                {label}
            </div>
            <div className="min-w-0">{children}</div>
        </div>
    );
}

function OutreachDetails({
    organizationId,
    outreach,
    timezone,
    mutateOutreach,
}: OutreachDetailsProps) {
    const [isResolving, setIsResolving] = useState(false);
    const [clockTimeInput, setClockTimeInput] = useState('');
    const [showResolveForm, setShowResolveForm] = useState(false);
    const isMissedClockReminder =
        outreach.status === 'unfilled' &&
        (outreach.outreach_type === 'clock_in' ||
            outreach.outreach_type === 'clock_out');
    const clockLabel =
        outreach.outreach_type === 'clock_in' ? 'clock-in' : 'clock-out';
    const isMobile = useIsMobile();
    const clockReminderResolution = getClockReminderResolution(
        outreach,
        timezone,
    );
    const clockReminderResolutionLabel = clockReminderResolution
        ? formatClockReminderResolution(clockReminderResolution)
        : null;

    const handleResolve = useCallback(async () => {
        if (!organizationId || !clockTimeInput) {
            return;
        }
        setIsResolving(true);
        try {
            // Build a UTC ISO datetime from the shift date and the
            // user-entered time (which is in the org timezone).
            const shiftDate = outreach.shifts[0]?.shift_start_time;
            let clockTimeIso = clockTimeInput;
            if (shiftDate && !clockTimeInput.includes('T')) {
                const dateStr = getDateStringInTimezone(
                    new Date(shiftDate),
                    timezone,
                );
                clockTimeIso = zonedTimeToUtcIso(
                    `${dateStr}T${clockTimeInput}`,
                    timezone,
                );
            }
            await getApiClient().post({
                url: '/organizations/{organization_id}/outreaches/{outreach_id}/resolve-clock-time',
                pathParams: {
                    organization_id: organizationId,
                    outreach_id: outreach.id,
                },
                body: { clock_time: clockTimeIso },
            });
            await mutateOutreach();
            setShowResolveForm(false);
            setClockTimeInput('');
            toast.success(`Resolved with ${clockLabel} time`, {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
        } catch {
            toast.error(`Failed to resolve ${clockLabel} time`, {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
        } finally {
            setIsResolving(false);
        }
    }, [
        organizationId,
        outreach.id,
        outreach.shifts,
        clockTimeInput,
        clockLabel,
        timezone,
        mutateOutreach,
    ]);

    return (
        <div className="border-t border-b px-4 py-5 sm:px-6 sm:py-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-medium">Outreach Details</h2>
            </div>
            {isMobile ? (
                <div className="space-y-3">
                    <MobileDetailCard label="Status">
                        <div className="flex flex-col items-start gap-2">
                            {(outreach.outreach_type === 'clock_in' ||
                                outreach.outreach_type === 'clock_out') && (
                                <ClockReminderOutcomeBadge
                                    outreachType={outreach.outreach_type}
                                    status={outreach.status}
                                    actualClockTime={
                                        (outreach.outreach_metadata?.[
                                            'actual_clock_time'
                                        ] as string | undefined) ?? null
                                    }
                                    timezone={timezone}
                                />
                            )}
                            <span className="text-muted-foreground text-xs">
                                Reported on{' '}
                                {formatDateAndTime(
                                    outreach.created_at,
                                    timezone,
                                )}
                            </span>
                            {isMissedClockReminder && !showResolveForm && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowResolveForm(true)}
                                >
                                    Enter {clockLabel} time
                                </Button>
                            )}
                            {isMissedClockReminder && showResolveForm && (
                                <div className="flex flex-wrap items-center gap-2">
                                    <input
                                        type="time"
                                        value={clockTimeInput}
                                        onChange={(e) =>
                                            setClockTimeInput(e.target.value)
                                        }
                                        className="border-input bg-background rounded-md border px-2 py-1 text-sm"
                                    />
                                    <Button
                                        variant="default"
                                        size="sm"
                                        onClick={handleResolve}
                                        disabled={
                                            isResolving || !clockTimeInput
                                        }
                                    >
                                        {isResolving ? (
                                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                        ) : null}
                                        Save
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setShowResolveForm(false);
                                            setClockTimeInput('');
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            )}
                        </div>
                    </MobileDetailCard>
                    {organizationId ? (
                        <OutreachSubscribersPanel
                            organizationId={organizationId}
                            outreachId={outreach.id}
                        />
                    ) : null}
                    <MobileDetailCard label="Type">
                        <OutreachTypeBadge
                            outreachType={outreach.outreach_type}
                        />
                    </MobileDetailCard>
                    {!!outreach.outreach_metadata['callout_reason'] && (
                        <MobileDetailCard label="Open Shift Reason">
                            <div className="text-sm break-words whitespace-normal">
                                {
                                    outreach.outreach_metadata[
                                        'callout_reason'
                                    ] as string
                                }
                            </div>
                        </MobileDetailCard>
                    )}
                    {clockReminderResolutionLabel ? (
                        <MobileDetailCard label="Resolution Reason">
                            <div className="text-sm break-words whitespace-normal">
                                {clockReminderResolutionLabel}
                            </div>
                        </MobileDetailCard>
                    ) : null}
                    <MobileDetailCard label="Steps">
                        <OutreachStepsSummary outreachSteps={outreach.steps} />
                    </MobileDetailCard>
                </div>
            ) : (
                <Table>
                    <TableBody>
                        <TableRow className="border-0">
                            <TableCell className="text-muted-foreground w-[140px] pl-0 font-medium">
                                Status
                            </TableCell>
                            <TableCell className="border-0">
                                <div className="flex flex-wrap items-center gap-3">
                                    {(outreach.outreach_type === 'clock_in' ||
                                        outreach.outreach_type ===
                                            'clock_out') && (
                                        <ClockReminderOutcomeBadge
                                            outreachType={
                                                outreach.outreach_type
                                            }
                                            status={outreach.status}
                                            actualClockTime={
                                                (outreach.outreach_metadata?.[
                                                    'actual_clock_time'
                                                ] as string | undefined) ?? null
                                            }
                                            timezone={timezone}
                                        />
                                    )}
                                    <span className="text-muted-foreground text-xs">
                                        Reported on{' '}
                                        {formatDateAndTime(
                                            outreach.created_at,
                                            timezone,
                                        )}
                                    </span>
                                    {isMissedClockReminder &&
                                        !showResolveForm && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    setShowResolveForm(true)
                                                }
                                            >
                                                Enter {clockLabel} time
                                            </Button>
                                        )}
                                    {isMissedClockReminder &&
                                        showResolveForm && (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="time"
                                                    value={clockTimeInput}
                                                    onChange={(e) =>
                                                        setClockTimeInput(
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="border-input bg-background rounded-md border px-2 py-1 text-sm"
                                                />
                                                <Button
                                                    variant="default"
                                                    size="sm"
                                                    onClick={handleResolve}
                                                    disabled={
                                                        isResolving ||
                                                        !clockTimeInput
                                                    }
                                                >
                                                    {isResolving ? (
                                                        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                    ) : null}
                                                    Save
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setShowResolveForm(
                                                            false,
                                                        );
                                                        setClockTimeInput('');
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )}
                                </div>
                            </TableCell>
                        </TableRow>
                        {organizationId ? (
                            <OutreachSubscribersPanel
                                displayMode="detailRow"
                                organizationId={organizationId}
                                outreachId={outreach.id}
                            />
                        ) : null}
                        <TableRow className="border-0">
                            <TableCell className="text-muted-foreground w-[140px] pl-0 font-medium">
                                Type
                            </TableCell>
                            <TableCell className="border-0">
                                <OutreachTypeBadge
                                    outreachType={outreach.outreach_type}
                                />
                            </TableCell>
                        </TableRow>
                        {!!outreach.outreach_metadata['callout_reason'] && (
                            <TableRow className="border-0">
                                <TableCell className="text-muted-foreground w-[140px] pl-0 font-medium">
                                    Open Shift Reason
                                </TableCell>
                                <TableCell className="border-0" colSpan={2}>
                                    <div className="text-sm break-words whitespace-normal">
                                        {
                                            outreach.outreach_metadata[
                                                'callout_reason'
                                            ] as string
                                        }
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {clockReminderResolutionLabel ? (
                            <TableRow className="border-0">
                                <TableCell className="text-muted-foreground w-[140px] pl-0 font-medium">
                                    Resolution Reason
                                </TableCell>
                                <TableCell className="border-0" colSpan={2}>
                                    <div className="text-sm break-words whitespace-normal">
                                        {clockReminderResolutionLabel}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : null}
                        <TableRow className="border-0">
                            <TableCell className="text-muted-foreground w-[140px] pl-0 font-medium">
                                <div className="flex items-center">Steps</div>
                            </TableCell>
                            <TableCell className="border-0">
                                <div className="space-y-4">
                                    <OutreachStepsSummary
                                        outreachSteps={outreach.steps}
                                    />
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            )}
        </div>
    );
}

type OutreachStepSummaryProps = {
    readonly outreachSteps: ReadonlyArray<OutreachStep>;
};

function OutreachStepsSummary({ outreachSteps }: OutreachStepSummaryProps) {
    function StepConnector() {
        return (
            <span className="bg-border block h-px w-16" aria-hidden="true" />
        );
    }

    return (
        <div className="flex flex-wrap items-center gap-3">
            {outreachSteps.map((step, index) => (
                <React.Fragment key={`${step}-${index}`}>
                    {index > 0 && <StepConnector />}
                    <Badge
                        variant="outline"
                        className={cn(
                            'flex items-center gap-1 text-xs tracking-wide',
                            step.step_type === 'voice'
                                ? 'capitalize'
                                : 'uppercase',
                        )}
                    >
                        <span>{step.step_type}</span>
                    </Badge>
                </React.Fragment>
            ))}
        </div>
    );
}

export function ClockReminderOutreachDetail({
    outreach,
    mutateOutreach,
    selectedContactAttemptId: selectedContactAttemptIdProp,
}: ClockReminderOutreachDetailProps) {
    const navigate = useNavigate();
    const selectedContactAttemptId = selectedContactAttemptIdProp ?? null;
    const [selectedCaregiverDetailId, setSelectedCaregiverDetailId] = useState<
        string | null
    >(null);
    const organizationId = useOrganizationId();
    const { timezone } = useOrganizationTimezone();
    const { data: user } = useUser({
        reason: 'clock-reminder-outreach-detail',
    });
    const isDevelopment = getRunEnvironment() === 'development';
    const isMobile = useIsMobile();

    const { data: organization } = useOrganization(organizationId, {
        reason: 'clock-reminder-outreach-detail',
    });
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        { reason: 'clock-reminder-outreach-detail' },
    );
    const { data: clients } = useOrganizationClients(organizationId, {
        reason: 'clock-reminder-outreach-detail',
    });

    const getShiftDuration = (startTime: string, endTime: string) => {
        const start = new Date(startTime);
        const end = new Date(endTime);
        const durationMs = end.getTime() - start.getTime();
        const durationHours = durationMs / (1000 * 60 * 60);
        return durationHours.toFixed(1);
    };

    const {
        data: contactAttempts,
        isLoading: isLoadingContactAttempts,
        error: contactAttemptsError,
        mutate: mutateContactAttempts,
    } = useContactAttemptsRealtime(
        outreach.id,
        'clock-reminder-outreach-detail',
    );

    const [caregiverResponseOverrides, setCaregiverResponseOverrides] =
        useState<Map<string, CaregiverResponseType | null>>(new Map());

    const clientById = useMemo(() => {
        const map = new Map<string, OrgClientRead>();
        for (const client of clients ?? []) {
            map.set(client.id, client);
        }
        return map;
    }, [clients]);

    const primaryShift = outreach.shifts[0];
    const clientExternalLink = useMemo(() => {
        if (!primaryShift?.client_id) {
            return null;
        }
        const client = clientById.get(primaryShift.client_id);
        if (!client?.external_id || !client?.source_system) {
            return null;
        }
        const dataSourceAccount = dataSourceAccounts?.find(
            (account) => account.type === client.source_system,
        );
        if (!dataSourceAccount) {
            return null;
        }
        const externalUrl = generateExternalClientUrl(
            client.source_system,
            client.external_id,
            dataSourceAccount.base_url,
        );
        const systemName = getDataSourceDisplayName(client.source_system);
        return { externalUrl, systemName };
    }, [clientById, dataSourceAccounts, primaryShift?.client_id]);

    const showClientLinkPreview =
        isDevelopment && !!primaryShift?.client_id && !clientExternalLink;
    const clientPhoneNumber = primaryShift?.client_id
        ? (clientById.get(primaryShift.client_id)?.phone_number ?? null)
        : null;

    useEffect(() => {
        setCaregiverResponseOverrides(new Map());
    }, [contactAttempts]);

    const contactAttemptGroups = useMemo(() => {
        if (!contactAttempts || contactAttempts.length === 0) {
            return [] as Array<ContactAttemptGroup>;
        }

        const groupsByCaregiver = new Map<
            string,
            { caregiver: OrgCaregiverRecord; attempts: Array<ContactAttempt> }
        >();

        for (const attempt of contactAttempts) {
            const caregiverId = attempt.caregiver_id;
            const existingGroup = groupsByCaregiver.get(caregiverId);

            if (existingGroup) {
                existingGroup.attempts.push(attempt);
            } else {
                groupsByCaregiver.set(caregiverId, {
                    caregiver: attempt.org_caregiver_record,
                    attempts: [attempt],
                });
            }
        }

        return Array.from(groupsByCaregiver.values()).map((group) => {
            const computedResponse = getOverallCaregiverResponse(
                group.attempts,
            );
            const hasOverride = caregiverResponseOverrides.has(
                group.caregiver.id,
            );
            const overrideValue = caregiverResponseOverrides.get(
                group.caregiver.id,
            );

            return {
                caregiver: group.caregiver,
                attempts: group.attempts,
                overallResponse: hasOverride
                    ? (overrideValue ?? null)
                    : computedResponse,
                recordedResponse: computedResponse,
                hasOverride,
            };
        });
    }, [caregiverResponseOverrides, contactAttempts]);

    const contactAttemptsById = useMemo(() => {
        if (!contactAttempts || contactAttempts.length === 0) {
            return new Map<string, ContactAttempt>();
        }

        return new Map(
            contactAttempts.map((attempt) => [attempt.id, attempt] as const),
        );
    }, [contactAttempts]);

    // Extract voice call IDs that have S3 recordings for batch fetching
    const voiceCallIdsForRecordings = useMemo(() => {
        if (!contactAttempts || contactAttempts.length === 0) {
            return null;
        }

        const ids: Array<string> = [];
        for (const attempt of contactAttempts) {
            const voiceCall = attempt.voice_call;
            if (!voiceCall?.id || !voiceCall.recording_url) {
                continue;
            }
            // Only include S3 URLs that need pre-signed URL generation
            const isS3Url =
                voiceCall.recording_url.includes('s3.amazonaws.com') ||
                voiceCall.recording_url.startsWith('s3://');
            if (isS3Url) {
                ids.push(voiceCall.id);
            }
        }

        return ids.length > 0 ? ids : null;
    }, [contactAttempts]);

    const { data: recordingUrls } = useVoiceCallRecordingUrls(
        organizationId,
        voiceCallIdsForRecordings,
    );

    const selectedCaregiverGroup = useMemo(() => {
        if (!selectedContactAttemptId) {
            return null;
        }

        const selectedAttempt = contactAttemptsById.get(
            selectedContactAttemptId,
        );

        if (!selectedAttempt) {
            return null;
        }

        return (
            contactAttemptGroups.find(
                (group) => group.caregiver.id === selectedAttempt.caregiver_id,
            ) ?? null
        );
    }, [contactAttemptGroups, contactAttemptsById, selectedContactAttemptId]);

    const openContactAttemptModal = useCallback(
        (contactAttemptId: string) => {
            navigate({
                to: '/outreaches/$callout-id',
                params: { 'callout-id': outreach.id },
                search: (previousSearch) => ({
                    organizationId: previousSearch.organizationId,
                    mode:
                        previousSearch.mode === 'live' ||
                        previousSearch.mode === 'sandbox'
                            ? previousSearch.mode
                            : undefined,
                    contactAttemptId,
                }),
            });
        },
        [outreach.id, navigate],
    );

    const closeContactAttemptModal = useCallback(() => {
        navigate({
            to: '/outreaches/$callout-id',
            params: { 'callout-id': outreach.id },
            search: (previousSearch) => ({
                organizationId: previousSearch.organizationId,
                mode:
                    previousSearch.mode === 'live' ||
                    previousSearch.mode === 'sandbox'
                        ? previousSearch.mode
                        : undefined,
            }),
        });
    }, [outreach.id, navigate]);

    useEffect(() => {
        if (
            selectedContactAttemptId &&
            !isLoadingContactAttempts &&
            contactAttempts &&
            !contactAttemptsById.has(selectedContactAttemptId)
        ) {
            closeContactAttemptModal();
        }
    }, [
        closeContactAttemptModal,
        contactAttempts,
        contactAttemptsById,
        isLoadingContactAttempts,
        selectedContactAttemptId,
    ]);

    const [deletingContactAttemptId, setDeletingContactAttemptId] = useState<
        string | null
    >(null);
    const [showAddCaregiversModal, setShowAddCaregiversModal] = useState(false);
    const [showCancelOutreachDialog, setShowCancelOutreachDialog] =
        useState(false);
    const [showPendingMessagesDialog, setShowPendingMessagesDialog] =
        useState(false);
    const [assignAndFillModalState, setAssignAndFillModalState] = useState<{
        isOpen: boolean;
        caregiver: OrgCaregiverRecord | null;
        assignmentType: ShiftAssignmentType;
        rateName: string | null;
    }>({
        isOpen: false,
        caregiver: null,
        assignmentType: 'single',
        rateName: null,
    });
    const [showEditShiftModal, setShowEditShiftModal] = useState(false);
    const [showStepsPreviewModal, setShowStepsPreviewModal] = useState(false);

    // Get the pending-message count for this outreach.
    const {
        data: pendingMessagesCount,
        isLoading: isLoadingPendingMessagesCount,
    } = useOrganizationPendingMessagesCountRealtime(
        'clock-reminder-outreach-detail',
        {
            organizationId,
            domain: 'callout',
            domainId: outreach.id,
        },
    );

    // Extract list of caregiver IDs that have already been contacted
    const contactedCaregiverIds = useMemo(() => {
        if (!contactAttempts || contactAttempts.length === 0) {
            return [];
        }

        // Get unique caregiver IDs from contact attempts
        const uniqueCaregiverIds = new Set<string>();
        contactAttempts.forEach((attempt) => {
            uniqueCaregiverIds.add(attempt.caregiver_id);
        });

        return Array.from(uniqueCaregiverIds);
    }, [contactAttempts]);

    const handleDeleteContactAttempt = useCallback(
        async (contactAttemptId: string) => {
            if (!organizationId || !outreach.id || !isDevelopment) {
                return;
            }

            setDeletingContactAttemptId(contactAttemptId);
            try {
                await getApiClient().delete({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/contact-attempts/{contact_attempt_id}',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreach.id,
                        contact_attempt_id: contactAttemptId,
                    },
                });
                toast.success('Contact attempt deleted', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                await mutateContactAttempts();
            } catch (error) {
                toast.error('Failed to delete contact attempt', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                console.error('Error deleting contact attempt:', error);
            } finally {
                setDeletingContactAttemptId(null);
            }
        },
        [organizationId, outreach.id, isDevelopment, mutateContactAttempts],
    );

    const { data: stagedMessages } = useOrganizationPendingMessagesRealtime(
        'clock-reminder-outreach-detail',
        false,
        {
            organizationId,
            domain: 'callout',
            domainId: outreach.id,
            conversationIdFilter:
                contactAttempts
                    ?.filter(
                        (attempt) =>
                            attempt.delivery_mode === 'sms' &&
                            attempt.conversation_id !== null,
                    )
                    .map((attempt) => attempt.conversation_id)
                    .filter((id): id is string => Boolean(id)) ?? undefined,
        },
    );

    const hasPendingMessageForCaregiver = useCallback(
        (caregiverId: string) => {
            if (!stagedMessages || !contactAttempts) {
                return false;
            }

            // Get conversation IDs for this specific caregiver's SMS attempts
            const caregiverConversationIds = contactAttempts
                .filter(
                    (attempt) =>
                        attempt.caregiver_id === caregiverId &&
                        attempt.delivery_mode === 'sms' &&
                        attempt.conversation_id !== null,
                )
                .map((attempt) => attempt.conversation_id)
                .filter((id): id is string => id !== null);

            if (caregiverConversationIds.length === 0) {
                return false;
            }

            // Check if there are pending messages for any of this caregiver's conversations
            return stagedMessages.some(
                (msg) =>
                    msg.status === 'pending' &&
                    caregiverConversationIds.includes(msg.conversation_id),
            );
        },
        [stagedMessages, contactAttempts],
    );

    const displayCaregiverNamesLastFirst =
        organization?.general_settings?.display_caregiver_names_last_first ??
        false;
    const sortByLastName =
        organization?.features?.last_name_sorting_enabled === true;
    const clientReferenceTerm =
        organization?.general_settings?.client_reference_term ?? 'client';
    const clientReferenceTermTitle = `${clientReferenceTerm
        .charAt(0)
        .toUpperCase()}${clientReferenceTerm.slice(1)}`;

    const formatCaregiverDisplayName = useCallback(
        (name: string) =>
            formatCaregiverName(name, displayCaregiverNamesLastFirst),
        [displayCaregiverNamesLastFirst],
    );

    // Get caregiver IDs that have been contacted
    const caregiverIdsForBlockedCheck = useMemo(() => {
        if (!contactAttempts || contactAttempts.length === 0) {
            return null;
        }
        const uniqueIds = new Set<string>();
        for (const attempt of contactAttempts) {
            uniqueIds.add(attempt.caregiver_id);
        }
        return Array.from(uniqueIds);
    }, [contactAttempts]);

    const { data: blockedCaregivers, mutate: mutateBlockedCaregivers } =
        useBlockedCaregivers(organizationId, caregiverIdsForBlockedCheck);

    const blockedCaregiverMap = useMemo(() => {
        const map = new Map<string, Array<BlockedCaregiverInfo>>();
        for (const blocked of blockedCaregivers ?? []) {
            const existing = map.get(blocked.caregiver_id);
            if (existing) {
                existing.push(blocked);
            } else {
                map.set(blocked.caregiver_id, [blocked]);
            }
        }
        return map;
    }, [blockedCaregivers]);

    const contactAttemptsColumns = useMemo(
        () =>
            createContactAttemptsColumns(
                timezone,
                hasPendingMessageForCaregiver,
                formatCaregiverDisplayName,
                {
                    isDevelopment,
                    onDeleteContactAttempt: handleDeleteContactAttempt,
                    deletingContactAttemptId,
                    outreachSteps: isActiveOutreach(outreach?.status)
                        ? outreach?.steps
                        : undefined,
                    blockedCaregiverMap,
                    onCaregiverUnblocked: () => mutateBlockedCaregivers(),
                    onCaregiverClick: setSelectedCaregiverDetailId,
                    recordingUrls,
                    dataSourceAccounts,
                    sortByLastName,
                },
            ),
        [
            timezone,
            hasPendingMessageForCaregiver,
            formatCaregiverDisplayName,
            isDevelopment,
            handleDeleteContactAttempt,
            deletingContactAttemptId,
            outreach?.steps,
            blockedCaregiverMap,
            mutateBlockedCaregivers,
            recordingUrls,
            outreach?.status,
            dataSourceAccounts,
            sortByLastName,
        ],
    );

    const renderContactAttemptMobileCard = useCallback(
        (group: ContactAttemptGroup) => {
            const latestAttempt = group.attempts[0];

            return (
                <div className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="min-w-0">
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setSelectedCaregiverDetailId(
                                            group.caregiver.id,
                                        );
                                    }}
                                    className="block w-full truncate text-left text-base font-semibold underline underline-offset-2"
                                >
                                    {formatCaregiverDisplayName(
                                        group.caregiver.name,
                                    )}
                                </button>
                            </div>
                            <div className="text-muted-foreground text-sm">
                                {group.attempts.length}{' '}
                                {group.attempts.length === 1
                                    ? 'attempt'
                                    : 'attempts'}
                                {group.caregiver.city
                                    ? ` · ${group.caregiver.city}`
                                    : ''}
                            </div>
                        </div>
                        <Badge variant="outline" className="shrink-0">
                            {getCaregiverResponseLabel(group.overallResponse)}
                        </Badge>
                    </div>
                    {latestAttempt ? (
                        <div className="bg-background rounded-md border p-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                    {getDeliveryModeDisplayName(
                                        latestAttempt.delivery_mode,
                                    )}
                                </Badge>
                                <ContactAttemptStatusBadge
                                    status={latestAttempt.outreach_status}
                                    deliveryMode={latestAttempt.delivery_mode}
                                    communicationDetails={
                                        latestAttempt.communication_details
                                    }
                                />
                                <span className="text-muted-foreground text-xs">
                                    {formatDateAndTime(
                                        latestAttempt.created_at,
                                        timezone,
                                    )}
                                </span>
                            </div>
                            <div className="text-muted-foreground mt-2 text-xs break-words">
                                {shouldShowSummaryPending(latestAttempt)
                                    ? 'Generating summary...'
                                    : getContactAttemptDetailsText(
                                          latestAttempt,
                                      )}
                            </div>
                            {isActiveOutreach(outreach.status) &&
                            outreach.steps ? (
                                <div className="mt-2 text-xs">
                                    <NextAttemptCountdown
                                        attempt={latestAttempt}
                                        outreachSteps={outreach.steps}
                                    />
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="text-muted-foreground text-xs">
                        Tap to view full attempt history.
                    </div>
                </div>
            );
        },
        [formatCaregiverDisplayName, outreach.status, outreach.steps, timezone],
    );

    const originalCaregiver = primaryShift
        ? outreach.original_caregivers?.[primaryShift.id]
        : undefined;
    const hasShiftLocation = Boolean(
        primaryShift?.patient_address ||
        primaryShift?.patient_city ||
        primaryShift?.patient_state,
    );

    return (
        <div>
            <div className="hidden border-b p-3 md:block">
                <div className="flex items-center justify-between">
                    <Link
                        to="/outreaches"
                        search={{ tab: 'reminders' }}
                        className="text-muted-foreground hover:text-foreground inline-flex items-center text-sm"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back to
                        Outreaches
                    </Link>
                </div>
            </div>
            <div className="mx-auto">
                <div className="px-6 py-6">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-lg font-medium">Shift Details</h2>
                        {isActiveOutreach(outreach.status) && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                aria-label="Stop clock reminder outreach"
                                title="Stop clock reminder outreach"
                                onClick={() =>
                                    setShowCancelOutreachDialog(true)
                                }
                            >
                                <X className="text-destructive mr-2 h-4 w-4" />
                                Stop
                            </Button>
                        )}
                    </div>
                    {isMobile ? (
                        <div className="space-y-3">
                            <MobileDetailCard label={clientReferenceTermTitle}>
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 space-y-1">
                                            <div className="break-words">
                                                {primaryShift?.patient_name ??
                                                    '—'}
                                            </div>
                                            <div className="text-muted-foreground text-xs">
                                                Phone:{' '}
                                                {clientPhoneNumber || '—'}
                                            </div>
                                        </div>
                                        {clientExternalLink ? (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                asChild={true}
                                                className="h-7 w-7 shrink-0"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <a
                                                    href={
                                                        clientExternalLink.externalUrl
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title={`View in ${clientExternalLink.systemName}`}
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                    <span className="sr-only">
                                                        View in{' '}
                                                        {
                                                            clientExternalLink.systemName
                                                        }
                                                    </span>
                                                </a>
                                            </Button>
                                        ) : showClientLinkPreview ? (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 shrink-0"
                                                disabled={true}
                                                title="View in EHR (preview)"
                                            >
                                                <ExternalLink className="h-4 w-4" />
                                                <span className="sr-only">
                                                    View in EHR (preview)
                                                </span>
                                            </Button>
                                        ) : null}
                                    </div>
                                    {primaryShift?.external_id ? (
                                        <div className="text-muted-foreground font-mono text-xs break-words">
                                            ID: {primaryShift.external_id}
                                        </div>
                                    ) : null}
                                    <div className="text-muted-foreground text-xs">
                                        Original Caregiver:{' '}
                                        {originalCaregiver
                                            ? formatCaregiverDisplayName(
                                                  originalCaregiver.name,
                                              )
                                            : 'Not specified'}
                                    </div>
                                </div>
                            </MobileDetailCard>
                            {Array.isArray(primaryShift?.patient_tags) && (
                                <MobileDetailCard
                                    label={`${clientReferenceTermTitle} Tags`}
                                >
                                    <div className="flex flex-wrap gap-1">
                                        {(primaryShift.patient_tags?.length ??
                                            0) > 0 ? (
                                            primaryShift.patient_tags!.map(
                                                (tag) => (
                                                    <span
                                                        key={tag}
                                                        className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-700/10 ring-inset"
                                                    >
                                                        {tag}
                                                    </span>
                                                ),
                                            )
                                        ) : (
                                            <span className="text-muted-foreground text-sm">
                                                N/A
                                            </span>
                                        )}
                                    </div>
                                </MobileDetailCard>
                            )}
                            <MobileDetailCard label="Date & Time">
                                {primaryShift ? (
                                    <Link
                                        to="/shifts"
                                        search={{
                                            date: getDateStringInTimezone(
                                                new Date(
                                                    primaryShift.shift_start_time,
                                                ),
                                                timezone,
                                            ),
                                            shiftId: primaryShift.id,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="hover:bg-muted -mx-2 -my-1 flex flex-col gap-2 rounded px-2 py-1 text-sm transition-colors"
                                    >
                                        <div className="flex items-center gap-1">
                                            <CalendarIcon className="h-3 w-3 shrink-0" />
                                            <span>
                                                {formatDateAndTime(
                                                    primaryShift.shift_start_time,
                                                    timezone,
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1">
                                            <Clock className="h-3 w-3 shrink-0" />
                                            <span>
                                                {formatTime(
                                                    primaryShift.shift_start_time,
                                                    timezone,
                                                )}{' '}
                                                -{' '}
                                                {formatTime(
                                                    primaryShift.shift_end_time,
                                                    timezone,
                                                )}
                                            </span>
                                            <span>
                                                (
                                                {getShiftDuration(
                                                    primaryShift.shift_start_time,
                                                    primaryShift.shift_end_time,
                                                )}{' '}
                                                hours)
                                            </span>
                                        </div>
                                    </Link>
                                ) : (
                                    <span className="text-sm">—</span>
                                )}
                            </MobileDetailCard>
                            {hasShiftLocation && (
                                <MobileDetailCard label="Shift Location">
                                    <div className="text-sm break-words">
                                        {primaryShift?.patient_address}
                                    </div>
                                </MobileDetailCard>
                            )}
                        </div>
                    ) : (
                        <Table>
                            <TableBody>
                                <TableRow className="border-0">
                                    <TableCell className="text-muted-foreground w-[140px] pl-0 font-medium">
                                        {clientReferenceTermTitle}
                                    </TableCell>
                                    <TableCell className="">
                                        <div className="flex flex-wrap items-center gap-4">
                                            <div className="flex flex-col">
                                                <span>
                                                    {
                                                        outreach.shifts[0]
                                                            ?.patient_name
                                                    }
                                                </span>
                                                <span className="text-muted-foreground text-xs">
                                                    Phone:{' '}
                                                    {clientPhoneNumber || '—'}
                                                </span>
                                            </div>
                                            {clientExternalLink ? (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    asChild={true}
                                                    className="h-7 w-7"
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <a
                                                        href={
                                                            clientExternalLink.externalUrl
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title={`View in ${clientExternalLink.systemName}`}
                                                    >
                                                        <ExternalLink className="h-4 w-4" />
                                                        <span className="sr-only">
                                                            View in{' '}
                                                            {
                                                                clientExternalLink.systemName
                                                            }
                                                        </span>
                                                    </a>
                                                </Button>
                                            ) : showClientLinkPreview ? (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7"
                                                    disabled={true}
                                                    title="View in EHR (preview)"
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                    <span className="sr-only">
                                                        View in EHR (preview)
                                                    </span>
                                                </Button>
                                            ) : null}
                                            {outreach.shifts[0]
                                                ?.external_id && (
                                                <span className="text-muted-foreground font-mono text-xs">
                                                    (ID:{' '}
                                                    {
                                                        outreach.shifts[0]
                                                            .external_id
                                                    }
                                                    )
                                                </span>
                                            )}
                                            <span className="text-muted-foreground text-xs">
                                                (Original Caregiver:{' '}
                                                {originalCaregiver
                                                    ? formatCaregiverDisplayName(
                                                          originalCaregiver.name,
                                                      )
                                                    : 'Not specified'}
                                                )
                                            </span>
                                        </div>
                                    </TableCell>
                                </TableRow>
                                {Array.isArray(
                                    outreach.shifts[0]?.patient_tags,
                                ) && (
                                    <TableRow className="border-0">
                                        <TableCell className="text-muted-foreground w-[140px] pl-0 font-medium">
                                            {clientReferenceTermTitle} Tags
                                        </TableCell>
                                        <TableCell colSpan={2}>
                                            <div className="flex flex-wrap gap-1">
                                                {(outreach.shifts[0]
                                                    .patient_tags?.length ??
                                                    0) > 0 ? (
                                                    outreach.shifts[0].patient_tags!.map(
                                                        (tag) => (
                                                            <span
                                                                key={tag}
                                                                className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-700/10 ring-inset"
                                                            >
                                                                {tag}
                                                            </span>
                                                        ),
                                                    )
                                                ) : (
                                                    <span className="text-muted-foreground text-sm">
                                                        N/A
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow className="border-0">
                                    <TableCell className="text-muted-foreground w-[140px] pl-0 font-medium">
                                        Date & Time
                                    </TableCell>
                                    <TableCell>
                                        {outreach.shifts[0] ? (
                                            <Link
                                                to="/shifts"
                                                search={{
                                                    date: getDateStringInTimezone(
                                                        new Date(
                                                            outreach.shifts[0]
                                                                .shift_start_time,
                                                        ),
                                                        timezone,
                                                    ),
                                                    shiftId:
                                                        outreach.shifts[0].id,
                                                }}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                className="hover:bg-muted -mx-2 -my-1 flex items-center gap-4 rounded px-2 py-1 transition-colors"
                                            >
                                                <div className="flex items-center">
                                                    <CalendarIcon className="mr-1 h-3 w-3" />
                                                    {formatDateAndTime(
                                                        outreach.shifts[0]
                                                            .shift_start_time,
                                                        timezone,
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Clock className="h-3 w-3 flex-shrink-0" />
                                                    <span className="whitespace-nowrap">
                                                        {formatTime(
                                                            outreach.shifts[0]
                                                                .shift_start_time,
                                                            timezone,
                                                        )}{' '}
                                                        -{' '}
                                                        {formatTime(
                                                            outreach.shifts[0]
                                                                .shift_end_time,
                                                            timezone,
                                                        )}
                                                    </span>
                                                    <span className="whitespace-nowrap">
                                                        (
                                                        {getShiftDuration(
                                                            outreach.shifts[0]
                                                                .shift_start_time,
                                                            outreach.shifts[0]
                                                                .shift_end_time,
                                                        )}{' '}
                                                        hours)
                                                    </span>
                                                </div>
                                            </Link>
                                        ) : (
                                            <div className="flex items-center gap-4">
                                                —
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                                {(outreach.shifts[0]?.patient_address ||
                                    outreach.shifts[0]?.patient_city ||
                                    outreach.shifts[0]?.patient_state) && (
                                    <TableRow className="border-0">
                                        <TableCell className="text-muted-foreground w-[140px] pl-0 font-medium">
                                            Shift Location
                                        </TableCell>
                                        <TableCell colSpan={2}>
                                            <div>
                                                {outreach.shifts[0]
                                                    ?.patient_address && (
                                                    <span>
                                                        {
                                                            outreach.shifts[0]
                                                                .patient_address
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </div>
                <OutreachDetails
                    organizationId={organizationId}
                    outreach={outreach}
                    timezone={timezone}
                    mutateOutreach={mutateOutreach}
                />
                {isOutreachStarted(outreach.status) && (
                    <React.Fragment>
                        <div className="my-2 flex items-center justify-end">
                            <div className="flex items-center gap-2 px-6">
                                {!isLoadingPendingMessagesCount &&
                                    (pendingMessagesCount ?? 0) > 0 && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                                setShowPendingMessagesDialog(
                                                    true,
                                                )
                                            }
                                        >
                                            <MessageSquareText className="mr-2 h-4 w-4" />
                                            Approve Messages (
                                            {pendingMessagesCount})
                                        </Button>
                                    )}
                            </div>
                        </div>
                        {contactAttemptsError ? (
                            <div className="text-destructive flex justify-center p-6">
                                Error loading contact attempts:{' '}
                                {contactAttemptsError.message}
                            </div>
                        ) : isLoadingContactAttempts ? (
                            <div className="flex justify-center p-6">
                                Loading contact attempts…
                            </div>
                        ) : contactAttempts?.length === 0 ? (
                            <div className="bg-muted/50 flex justify-center rounded-lg p-6">
                                <p className="text-muted-foreground text-sm">
                                    {isActiveOutreach(outreach.status)
                                        ? 'Please add caregivers to contact'
                                        : 'No contact attempts have been made yet.'}
                                </p>
                            </div>
                        ) : (
                            <div className="[&>div]:rounded-none [&>div]:border-x-0">
                                <DataTable
                                    pageSize={25}
                                    columns={contactAttemptsColumns}
                                    data={contactAttemptGroups}
                                    mobileRowsClassName="max-h-[28rem] overflow-y-auto overscroll-contain"
                                    renderMobileCard={
                                        renderContactAttemptMobileCard
                                    }
                                    onRowClick={(group) => {
                                        const attempts = group.attempts;

                                        if (attempts.length === 0) {
                                            return;
                                        }

                                        const [firstAttempt] = attempts;

                                        if (!firstAttempt) {
                                            return;
                                        }

                                        const matchingSelectedAttempt =
                                            selectedContactAttemptId
                                                ? attempts.find(
                                                      (attempt) =>
                                                          attempt.id ===
                                                          selectedContactAttemptId,
                                                  )
                                                : null;

                                        const attemptToOpen =
                                            matchingSelectedAttempt ??
                                            attempts.reduce<ContactAttempt>(
                                                (latest, attempt) =>
                                                    new Date(
                                                        attempt.created_at,
                                                    ).getTime() >
                                                    new Date(
                                                        latest.created_at,
                                                    ).getTime()
                                                        ? attempt
                                                        : latest,
                                                firstAttempt,
                                            );

                                        openContactAttemptModal(
                                            attemptToOpen.id,
                                        );
                                    }}
                                    initialSorting={[
                                        {
                                            id: 'caregiver.name',
                                            desc: false,
                                        },
                                    ]}
                                />
                            </div>
                        )}
                    </React.Fragment>
                )}
                {selectedCaregiverGroup && (
                    <CaregiverOutreachDetailModal
                        isOpen={true}
                        onClose={closeContactAttemptModal}
                        caregiver={selectedCaregiverGroup.caregiver}
                        attempts={selectedCaregiverGroup.attempts}
                        isAdmin={user?.admin}
                        outreachId={outreach.id}
                        recordingUrls={recordingUrls}
                    />
                )}
                {organizationId ? (
                    <CaregiverDetailModal
                        isOpen={selectedCaregiverDetailId !== null}
                        onClose={() => setSelectedCaregiverDetailId(null)}
                        organizationId={organizationId}
                        caregiverId={selectedCaregiverDetailId ?? undefined}
                    />
                ) : null}
            </div>
            <AddCaregiversModal
                isOpen={showAddCaregiversModal}
                onClose={() => setShowAddCaregiversModal(false)}
                outreach={outreach}
                contactedCaregiverIds={contactedCaregiverIds}
                onSuccess={() => {
                    mutateContactAttempts();
                    mutateOutreach();
                }}
            />
            <CancelOutreachDialog
                open={showCancelOutreachDialog}
                onOpenChange={setShowCancelOutreachDialog}
                outreach={outreach}
                organizationId={organizationId}
                timezone={timezone}
                organizationName={organization?.name}
                onSuccess={() => {
                    void mutateOutreach();
                    void mutateContactAttempts();
                }}
            />
            <PendingMessagesDialog
                organizationId={organizationId!}
                open={showPendingMessagesDialog}
                onOpenChange={setShowPendingMessagesDialog}
                domain="callout"
                domainId={outreach.id}
                showCareCoordinatorFilter={false}
            />
            {assignAndFillModalState.caregiver &&
                outreach.shifts[0] &&
                organization && (
                    <AssignAndFillConfirmationModal
                        isOpen={assignAndFillModalState.isOpen}
                        onClose={() => {
                            setAssignAndFillModalState((prev) => ({
                                ...prev,
                                isOpen: false,
                            }));
                        }}
                        caregiver={assignAndFillModalState.caregiver}
                        isLoading={false}
                        outreachId={outreach.id}
                        onSubmit={() => {
                            setAssignAndFillModalState((prev) => ({
                                ...prev,
                                isOpen: false,
                            }));
                            mutateOutreach();
                            mutateContactAttempts();
                        }}
                        outreachType={outreach.outreach_type}
                        shift={outreach.shifts[0]}
                        timezone={timezone}
                        formatCaregiverName={formatCaregiverDisplayName}
                        isAdmin={user?.admin}
                    />
                )}
            {outreach.shifts[0] && (
                <EditShiftModal
                    isOpen={showEditShiftModal}
                    onClose={() => {
                        setShowEditShiftModal(false);
                    }}
                    shift={outreach.shifts[0]}
                    organizationId={organizationId ?? ''}
                    onSuccess={() => {
                        mutateOutreach();
                        setShowEditShiftModal(false);
                    }}
                    outreachId={outreach.id}
                />
            )}
            <OutreachStepsModal
                isOpen={showStepsPreviewModal}
                onClose={() => setShowStepsPreviewModal(false)}
                outreach={outreach}
                onSuccess={() => {
                    mutateOutreach();
                }}
            />
        </div>
    );
}
